import * as L from 'leaflet';
import * as PIXI from 'pixi.js';
import 'leaflet-pixi-overlay';

PIXI.utils.skipHello();

export interface PixiPoint {
  lat: number;
  lng: number;
  props: { [key: string]: any };
}

/** Radius in screen px: one size for every zoom, or zoom -> size stops, e.g. { 5: 2, 8: 3, 11: 5 } */
export type PixiRadius = number | { [zoom: number]: number };

export interface PixiPointsLayerOptions {
  /** Fill colour ('#rrggbb') for a point's properties; return null to leave the point out */
  getColor: (props: any) => string | null;
  /** Popup HTML for a clicked point's properties; omit (or return null) for no popup */
  getPopupContent?: (props: any) => string | null;
  /** Point radius in screen pixels; with zoom stops it is interpolated between them and held beyond them */
  radius?: PixiRadius;
  /** Outline colour ('#rrggbb') shared by every point */
  borderColor?: string;
  /** Outline width in screen pixels; 0 for no outline */
  borderWidth?: number;
  pane?: string;
  /** Pane the point popup opens in; use one stacked above the other layers' panes so the popup isn't hidden */
  popupPane?: string;
  /** Any other config (legendName, group, name...) is kept on layer.options like other overlays */
  [key: string]: any;
}

// Circle textures (one per fill colour) are drawn at the point's radius but never below this, then scaled
// per zoom. Drawing at least at the real size keeps the outline >= 1px inside the texture; Pixi drops
// thinner lines, which made outlines vanish on large points drawn small and stretched up.
const MIN_TEXTURE_RADIUS = 8;
// Extra screen pixels around a point that still count as clicking it
const CLICK_TOLERANCE = 3;

const PixiOverlay = (L as any).PixiOverlay;

function textureRadius(radius: number) {
  return Math.max(MIN_TEXTURE_RADIUS, radius);
}

/**
 * Point layer rendered with Leaflet.PixiOverlay (WebGL), for datasets too large for L.circle/L.marker.
 * Behaves like any Leaflet layer: map.addLayer/removeLayer/hasLayer all work. Removing it from the
 * map releases its WebGL renderer, so layers recreated on every hazard change never exhaust the
 * browser's WebGL context limit; adding it back rebuilds the sprites from the kept points.
 */
const PixiPointsLayerClass = PixiOverlay.extend({
  options: {
    radius: 4,
    borderColor: '#333333',
    borderWidth: 1,
    popupPane: 'popupPane',
    padding: 0.1,
    // html2canvas/dom-to-image (map & PDF export) capture a WebGL canvas blank without this
    preserveDrawingBuffer: true,
    // Clicks are hit-tested in _onClickCapture; Pixi's own interaction listeners aren't needed
    destroyInteractionManager: true
  },

  initialize(points: PixiPoint[], options: PixiPointsLayerOptions) {
    this._points = points;
    this._sprites = [];
    this._spritePoints = [];
    PixiOverlay.prototype.initialize.call(this, (utils: any) => this._draw(utils), new PIXI.Container(), options);
    this._onClickCapture = this._onClickCapture.bind(this);
  },

  onAdd(map: L.Map) {
    PixiOverlay.prototype.onAdd.call(this, map);
    // The canvas spans the whole map, so let clicks through to the layers underneath (e.g. clickable
    // boundaries for drill-down). Points get first pick in the capture phase instead (see _onClickCapture).
    this._container.style.pointerEvents = 'none';
    map.getContainer().addEventListener('click', this._onClickCapture, true);
  },

  onRemove(map: L.Map) {
    map.getContainer().removeEventListener('click', this._onClickCapture, true);
    PixiOverlay.prototype.onRemove.call(this, map);
    if (this._popup) {
      map.closePopup(this._popup);
      this._popup = null;
    }
    this._releaseGraphics();
  },

  _draw(utils: any) {
    const renderer = utils.getRenderer();
    const zoom = utils.getMap().getZoom();
    const radius = radiusAtZoom(this.options.radius, zoom);
    if (!this._textures) {
      this._buildSprites(utils, radius);
    } else if (radius !== this._textureRadius) {
      this._rebuildTextures(renderer, radius);
    }
    if (this._spriteZoom !== zoom) {
      const scale = radius / textureRadius(radius) / utils.getScale();
      this._sprites.forEach((sprite: PIXI.Sprite) => sprite.scale.set(scale));
      this._spriteZoom = zoom;
    }
    renderer.render(this._pixiContainer);
  },

  // Sprites share one texture per fill colour (fill and outline baked in), since a tint would recolour the outline too
  _buildSprites(utils: any, radius: number) {
    this._textures = {};
    this._spritesByColor = {};
    this._textureRadius = radius;
    this._points.forEach((point: PixiPoint) => {
      const color = this.options.getColor(point.props);
      if (!color) {
        return;
      }
      if (!this._textures[color]) {
        this._textures[color] = this._createCircleTexture(utils.getRenderer(), color, radius);
        this._spritesByColor[color] = [];
      }
      const position = utils.latLngToLayerPoint([point.lat, point.lng]);
      const sprite = new PIXI.Sprite(this._textures[color]);
      sprite.anchor.set(0.5);
      sprite.position.set(position.x, position.y);
      this._pixiContainer.addChild(sprite);
      this._sprites.push(sprite);
      this._spritePoints.push(point);
      this._spritesByColor[color].push(sprite);
    });
  },

  // The outline width is baked into the textures relative to the radius, so a new radius needs new textures
  // to keep the outline at borderWidth screen px (only a handful: one per colour)
  _rebuildTextures(renderer: any, radius: number) {
    for (const color in this._textures) {
      const texture = this._createCircleTexture(renderer, color, radius);
      this._spritesByColor[color].forEach((sprite: PIXI.Sprite) => sprite.texture = texture);
      this._textures[color].destroy(true);
      this._textures[color] = texture;
    }
    this._textureRadius = radius;
  },

  _createCircleTexture(renderer: any, fillColor: string, radius: number) {
    const toHex = (color: string) => parseInt(color.replace('#', ''), 16);
    const circle = new PIXI.Graphics();
    const drawRadius = textureRadius(radius);
    if (this.options.borderWidth > 0) {
      // Sprites are scaled by radius / drawRadius, so scale the outline inversely to keep it at borderWidth screen px
      circle.lineStyle(this.options.borderWidth * drawRadius / radius, toHex(this.options.borderColor), 1);
    }
    circle.beginFill(toHex(fillColor), 1).drawCircle(0, 0, drawRadius).endFill();
    const texture = renderer.generateTexture(circle);
    circle.destroy();
    return texture;
  },

  _releaseGraphics() {
    this._pixiContainer.removeChildren().forEach((sprite: PIXI.Sprite) => sprite.destroy());
    this._sprites = [];
    this._spritePoints = [];
    this._spriteZoom = null;
    for (const color in this._textures) {
      this._textures[color].destroy(true);
    }
    this._textures = null;
    this._spritesByColor = null;
    this._textureRadius = null;
    [this._renderer, this._auxRenderer].forEach((renderer: any) => renderer && renderer.destroy(true));
    this._renderer = this._auxRenderer = null;
    // A null container makes PixiOverlay.onAdd create a fresh renderer when the layer is re-added
    this._container = null;
  },

  // Runs before the click reaches any layer below; when it lands on a point, the point's popup opens and the
  // click is stopped, so it doesn't also trigger whatever is underneath (e.g. a boundary drill-down)
  _onClickCapture(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!this.options.getPopupContent || !this._sprites.length || (this._map.dragging && this._map.dragging.moved())
      || (target.closest && target.closest('.leaflet-popup, .leaflet-control, .leaflet-marker-icon'))) {
      return;
    }
    const latlng = this._map.mouseEventToLatLng(e);
    const point = this._pointAt(latlng);
    if (!point) {
      return;
    }
    e.stopPropagation();
    const content = this.options.getPopupContent(point.props);
    if (content) {
      this._popup = L.popup({ pane: this.options.popupPane }).setLatLng([point.lat, point.lng]).setContent(content).openOn(this._map);
    }
  },

  _pointAt(latlng: L.LatLng): PixiPoint | null {
    const clicked = this.utils.latLngToLayerPoint(latlng);
    const tolerance = (this._textureRadius + CLICK_TOLERANCE) / this.utils.getScale();
    let nearestIndex = -1;
    let nearestDistance = tolerance * tolerance;
    this._sprites.forEach((sprite: PIXI.Sprite, index: number) => {
      const dx = sprite.x - clicked.x;
      const dy = sprite.y - clicked.y;
      const distance = dx * dx + dy * dy;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex < 0 ? null : this._spritePoints[nearestIndex];
  }
});

/** Radius for a zoom level: a fixed number as is, zoom stops interpolated linearly and clamped at both ends */
export function radiusAtZoom(radius: PixiRadius, zoom: number): number {
  if (typeof radius === 'number') {
    return radius;
  }
  const stops = Object.keys(radius).map(Number).sort((a, b) => a - b);
  if (zoom <= stops[0]) {
    return radius[stops[0]];
  }
  for (let i = 1; i < stops.length; i++) {
    if (zoom <= stops[i]) {
      const t = (zoom - stops[i - 1]) / (stops[i] - stops[i - 1]);
      return radius[stops[i - 1]] + t * (radius[stops[i]] - radius[stops[i - 1]]);
    }
  }
  return radius[stops[stops.length - 1]];
}

export function createPixiPointsLayer(points: PixiPoint[], options: PixiPointsLayerOptions): L.Layer {
  return new PixiPointsLayerClass(points, options);
}

export function isPixiPointsLayer(layer: any): boolean {
  return layer instanceof PixiPointsLayerClass;
}

/** Popup table of label/value rows; values are HTML-escaped and empty values are skipped */
export function pixiPopupTable(title: string, rows: [string, any][]): string {
  const escape = (value: any) => String(value).replace(/[&<>"']/g, ch => `&#${ch.charCodeAt(0)};`);
  const body = rows
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([label, value]) => `<tr><td style="font-weight:600;padding-right:8px">${escape(label)}</td><td>${escape(value)}</td></tr>`)
    .join('');
  return `<div style="font-weight:700;margin-bottom:4px">${escape(title)}</div><table>${body}</table>`;
}
